import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Facebook, Instagram, Youtube, MapPin, Phone } from "lucide-react";
import { useSiteContent } from "@/hooks/useContent";

export default function Footer() {
  const { content } = useSiteContent();
  const s = content?.settings || {};

  const socials = [
    s.facebook && { icon: Facebook, url: s.facebook, label: "Facebook" },
    s.instagram && { icon: Instagram, url: s.instagram, label: "Instagram" },
    s.youtube && { icon: Youtube, url: s.youtube, label: "YouTube" },
  ].filter(Boolean);

  return (
    <footer className="border-t border-white/10 bg-gg-black" data-testid="footer">
      <div className="overflow-hidden py-4 md:py-6 border-b border-white/10">
        <Marquee speed={40} gradient={false}><span className="font-heading text-3xl md:text-8xl font-black uppercase tracking-tighter text-white/[0.03] whitespace-nowrap">GG FITNESS &bull; {(content?.about?.name || "GORA GANGULY").toUpperCase()} &bull; TRAIN HARD &bull; NO EXCUSES &bull;&nbsp;</span></Marquee>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-10 md:gap-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            {s.logo && <img src={s.logo} alt="GG Fitness" className="h-10 md:h-12 mb-4" />}
            <p className="text-zinc-500 text-sm leading-relaxed tracking-wide max-w-xs">Transforming lives through science-backed fitness training. Based in {s.location_line1 || "Bolpur, Santiniketan"}, {s.location_line2 || "West Bengal"}.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <h4 className="font-heading text-sm uppercase tracking-widest text-white font-bold mb-4">Pages</h4>
            <div className="space-y-3">
              {[{l:"About",t:"/about"},{l:"Credentials",t:"/qualifications"},{l:"Personal Training",t:"/personal-training"},{l:"Nutrition",t:"/nutrition"},{l:"Online Training",t:"/online-training"},{l:"Reviews",t:"/reviews"},{l:"Gallery",t:"/gallery"},{l:"Transformations",t:"/transformations"},{l:"FAQ",t:"/faq"},{l:"Contact",t:"/contact"}].map((lk) => (
                <Link key={lk.t} to={lk.t} className="block text-zinc-500 text-sm hover:text-gg-red hover:translate-x-1 transition-all duration-300 tracking-wide">{lk.l}</Link>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <h4 className="font-heading text-sm uppercase tracking-widest text-white font-bold mb-4">Connect</h4>
            <div className="space-y-3 mb-6">
              <a href={`tel:+${s.whatsapp || "919153020904"}`} className="flex items-center gap-3 text-zinc-500 text-sm hover:text-white transition-colors"><Phone size={14} className="text-gg-red shrink-0" /> +{s.whatsapp || "91 91530 20904"}</a>
              <a href={`https://maps.google.com/?q=${s.maps_query || "Bolpur+Santiniketan"}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-500 text-sm hover:text-white transition-colors"><MapPin size={14} className="text-gg-red shrink-0" /> {s.location_line1 || "Bolpur, Santiniketan"}, {s.location_line2 || "WB"}</a>
            </div>
            <div className="flex gap-3">
              {socials.map((sc) => (
                <a key={sc.label} href={sc.url} target="_blank" rel="noopener noreferrer" data-testid={`social-${sc.label.toLowerCase()}`} className="w-10 h-10 border border-white/10 rounded-lg flex items-center justify-center text-zinc-500 hover:text-white hover:border-gg-red hover:bg-gg-red/10 transition-all duration-300 hover:scale-110"><sc.icon size={18} /></a>
              ))}
            </div>
          </motion.div>
        </div>
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-zinc-600 text-xs tracking-wide">&copy; {new Date().getFullYear()} GG Fitness. All rights reserved.</p>
          <p className="text-zinc-700 text-xs tracking-wide">Best Personal Trainer in Bolpur, Santiniketan | {content?.about?.name || "Gora Ganguly"}</p>
        </div>
      </div>
    </footer>
  );
}
